import React, { createContext, useState, useContext, useEffect, useCallback } from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("agritrade_user");
      const savedToken = localStorage.getItem("agritrade_token");

      if (savedUser && savedToken) {
        const parsedUser = JSON.parse(savedUser);

        const sanitizedUser = {
          ...parsedUser,
          kyc_verified: Number(parsedUser.kyc_verified) || 0,
          account_number: parsedUser.account_number || "",
          bank_name: parsedUser.bank_name || "",
          phone: parsedUser.phone || "",
          balance: Number(parsedUser.balance) || 0,
        };

        setUser(sanitizedUser);
        setToken(savedToken);
        setIsAuthenticated(true);
      }
    } catch (err) {
      console.error("Auth restoration failed:", err);
      localStorage.clear();
    }
    setLoading(false);
  }, []);

  const login = useCallback((userData) => {
    const rawUser = userData.user || userData; 
    
    const sanitizedUser = {
      ...rawUser,
      kyc_verified: Number(rawUser.kyc_verified) || 0,
      account_number: rawUser.account_number || "",
      bank_name: rawUser.bank_name || "",
      phone: rawUser.phone || "",
      balance: Number(rawUser.balance) || 0,
    };

    setUser(sanitizedUser);
    setToken(userData.token);
    setIsAuthenticated(true);

    localStorage.setItem("agritrade_user", JSON.stringify(sanitizedUser));
    localStorage.setItem("agritrade_token", userData.token);
  }, []);

  const updateUser = useCallback((updated) => {
    setUser((prevUser) => {
      if (!prevUser) return null;

      const newState = {
        ...prevUser,
        ...updated,
        // Ensure strictly snake_case for the entire app
        kyc_verified: updated.kyc_verified !== undefined ? Number(updated.kyc_verified) : prevUser.kyc_verified,
        balance: updated.balance !== undefined ? Number(updated.balance) : prevUser.balance,
        account_number: updated.account_number || updated.accountNumber || prevUser.account_number,
        bank_name: updated.bank_name || prevUser.bank_name,
      };
      
      localStorage.setItem("agritrade_user", JSON.stringify(newState));
      return newState;
    });
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.clear();
  }, []);

  useEffect(() => {
    const syncHandler = (e) => {
      if (e.key === "agritrade_user") {
        if (!e.newValue) logout();
        else {
            try {
                setUser(JSON.parse(e.newValue));
            } catch (err) {
                console.error("Storage sync parse error");
            }
        }
      }
    };
    window.addEventListener("storage", syncHandler);
    return () => window.removeEventListener("storage", syncHandler);
  }, [logout]);

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};